import { faBuilding } from "@fortawesome/free-regular-svg-icons";
import { faUnlink } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type FC, useMemo } from "react";
import { useApi } from "../../api/APIContext";
import type { Company, Project } from "../../api/dto";
import { UnlinkCompanyModal } from "../../modals/UnlinkCompanyModal";
import { useModal } from "../layout/ModalProvider";

interface CompanyWithProjectsParentsAndChildren extends Company {
  childCompanies: Company[];
  parentCompanies: Company[];
  projects: Project[];
}

interface CompanyListTileProps {
  company: CompanyWithProjectsParentsAndChildren;
  onClick?: () => void;
}
export const CompanyListTile: FC<CompanyListTileProps> = (props) => {
  const { inspections, exams } = useApi();
  const companyInspections = useMemo(
    () =>
      inspections.filter((i) => i.companyId === props.company.id && i.archivedAt !== null).length,
    [inspections, props.company],
  );
  const companyExams = useMemo(
    () => exams.filter((e) => e.companyId === props.company.id && e.archivedAt !== null).length,
    [exams, props.company],
  );
  return (
    <div
      className={`border border-slate-200 bg-white rounded ${props.onClick ? "hover:bg-brand-50 hover:border-brand-400 hover:shadow-md" : ""} cursor-default`}
      onClick={props.onClick}
    >
      {props.company.logo ? (
        <>
          <img
            src={`${props.company.logo}`}
            alt=""
            className={
              "h-36 w-full px-8 py-4 bg-white border-b border-inherit object-contain rounded-t"
            }
          />
        </>
      ) : (
        <>
          <div
            className={
              "h-36 w-full flex items-center justify-center bg-white border-b border-inherit"
            }
          >
            <FontAwesomeIcon icon={faBuilding} className={"text-slate-400 text-2xl"} />
          </div>
        </>
      )}
      {props.company.parentCompanies.length > 0 && (
        <>
          <div className={"font-medium text-xs text-slate-600 px-4 mt-4 -mb-3"}>
            Onderdeel van: {props.company.parentCompanies.map((c) => c.name).join(", ")}
          </div>
        </>
      )}
      <div className={"font-bold text-xl px-4 my-4"}>{props.company.name}</div>
      <div className={"grid grid-cols-3 gap-4 px-4 mb-4"}>
        <div>
          <div className={"text-xs text-slate-600 font-medium"}>Projecten</div>
          <div className={"font-medium"}>{props.company.projects.length}</div>
        </div>
        <div>
          <div className={"text-xs text-slate-600 font-medium"}>Tests</div>
          <div className={"font-medium"}>{companyExams}</div>
        </div>
        <div>
          <div className={"text-xs text-slate-600 font-medium"}>Inspecties</div>
          <div className={"font-medium"}>{companyInspections}</div>
        </div>
      </div>
    </div>
  );
};
export const ChildCompanyTile: FC<{
  company: Company;
  parentCompany: Company;
  onClick?: () => void;
}> = (props) => {
  const detachCompanyModal = useModal({
    title: "Loskoppelen",
    body: <UnlinkCompanyModal baseCompany={props.parentCompany} childCompany={props.company} />,
  });
  return (
    <div
      className={`border border-slate-200 bg-white group relative rounded ${props.onClick ? "hover:bg-brand-50 hover:border-brand-400 hover:shadow-md" : ""} cursor-default`}
      onClick={props.onClick}
    >
      {props.company.logo ? (
        <>
          <img
            src={`${props.company.logo}`}
            alt=""
            className={
              "h-24 w-full px-8 py-4 bg-white border-b border-inherit object-contain rounded-t"
            }
          />
        </>
      ) : (
        <>
          <div
            className={
              "h-24 w-full flex items-center justify-center bg-white border-b border-inherit"
            }
          >
            <FontAwesomeIcon icon={faBuilding} className={"text-slate-400 text-2xl"} />
          </div>
        </>
      )}
      <div className={"font-medium text-xs text-slate-600 px-4 mt-4 -mb-3"}>Dochterbedrijf</div>
      <div className={"font-bold text-base px-4 my-4"}>{props.company.name}</div>
      <button
        className="hidden group-hover:flex items-center justify-center absolute -top-2 -right-2 h-6 w-6 rounded-full border border-red-500 bg-red-700 hover:bg-red-500 hover:text-red-50 cursor-pointer text-white"
        type={"button"}
        onClick={(e) => {
          e.stopPropagation();
          detachCompanyModal.open();
        }}
      >
        <FontAwesomeIcon icon={faUnlink} className="text-xs" />
      </button>
    </div>
  );
};
export const ParentCompanyTile: FC<{
  company: Company;
  childCompany: Company;
  onClick?: () => void;
}> = (props) => {
  const detachCompanyModal = useModal({
    title: "Loskoppelen",
    body: <UnlinkCompanyModal baseCompany={props.childCompany} parentCompany={props.company} />,
  });
  return (
    <div
      className={`border border-slate-200 bg-white group relative rounded ${props.onClick ? "hover:bg-brand-50 hover:border-brand-400 hover:shadow-md" : ""} cursor-default`}
      onClick={props.onClick}
    >
      {props.company.logo ? (
        <>
          <img
            src={`${props.company.logo}`}
            alt=""
            className={
              "h-24 w-full px-8 py-4 bg-white border-b border-inherit object-contain rounded-t"
            }
          />
        </>
      ) : (
        <>
          <div
            className={
              "h-24 w-full flex items-center justify-center bg-white border-b border-inherit"
            }
          >
            <FontAwesomeIcon icon={faBuilding} className={"text-slate-400 text-2xl"} />
          </div>
        </>
      )}
      <div className={"font-medium text-xs text-slate-600 px-4 mt-4 -mb-3"}>Moederbedrijf</div>
      <div className={"font-bold text-base px-4 my-4"}>{props.company.name}</div>
      <button
        className="hidden group-hover:flex items-center justify-center absolute -top-2 -right-2 h-6 w-6 rounded-full border border-red-500 bg-red-700 hover:bg-red-500 hover:text-red-50 cursor-pointer text-white"
        type={"button"}
        onClick={(e) => {
          e.stopPropagation();
          detachCompanyModal.open();
        }}
      >
        <FontAwesomeIcon icon={faUnlink} className="text-xs" />
      </button>
    </div>
  );
};
